import { useMemo, useRef, useState, useEffect } from "react";
import useSeo from "../lib/useSeo";

import { db, storage } from "../lib/firebase";
import { addDoc, collection, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { CONTACT_EMAIL, REGIONAL_CONTACT } from "../lib/copy";
import { useRegion } from "../Region";

const SITE_URL = process.env.PUBLIC_URL;

const MAX_FILES = 6;
const MAX_MB_EACH = 6;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

function sanitizeFilename(name = "photo") {
  // keep it simple + safe for storage paths
  return name.replace(/[^\w.\-]+/g, "_");
}

export default function Contact() {
  const { region } = useRegion();
  const regionalContact = REGIONAL_CONTACT[region];
  useSeo({
    title: "Request a Restoration Evaluation | Evon Polishing",
    description:
      "Request a restoration evaluation for Catholic liturgical metalware. Share details and photos to begin.",
    canonical: `${SITE_URL}/contact`,
  });

  const formRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [files, setFiles] = useState([]); // File[]
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [uploadPct, setUploadPct] = useState(0);

  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [files]
  );

  useEffect(() => {
    // cleanup previews
    return () => previews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [previews]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validateFiles(nextList) {
    const combined = [...files, ...nextList];

    if (combined.length > MAX_FILES) {
      return `Max ${MAX_FILES} photos.`;
    }

    for (const f of nextList) {
      if (!ACCEPTED.includes(f.type)) {
        return "Only JPG, PNG, or WebP images are allowed.";
      }
      if (f.size > MAX_MB_EACH * 1024 * 1024) {
        return `Each image must be under ${MAX_MB_EACH}MB.`;
      }
    }

    return "";
  }

  function onPickFiles(e) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    const err = validateFiles(picked);
    if (err) {
      setStatus({ type: "error", msg: err });
      e.target.value = "";
      return;
    }

    setStatus({ type: "", msg: "" });
    setFiles((prev) => [...prev, ...picked]);

    // allow selecting the same file again later
    e.target.value = "";
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function onSubmit(e) {
    e.preventDefault();

    if (busy) return;

    setBusy(true);
    setUploadPct(0);
    setStatus({ type: "", msg: "" });

    try {
      // 1) Create Firestore doc first (gets requestId)
      const docRef = await addDoc(collection(db, "evaluationRequests"), {
        createdAt: serverTimestamp(),
        lastUpdatedAt: serverTimestamp(),
        status: "new",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        region,
        attachments: [],
        source: "public_contact_form",
      });

      // 2) Upload attachments (if any)
      let uploaded = [];

      if (files.length) {
        uploaded = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const safeName = sanitizeFilename(file.name);
          const path = `evaluationRequests/${docRef.id}/${Date.now()}_${safeName}`;
          const storageRef = ref(storage, path);

          await uploadBytes(storageRef, file, { contentType: file.type });
          const url = await getDownloadURL(storageRef);

          uploaded.push({
            path,
            url,
            name: file.name,
            type: file.type,
            size: file.size,
          });

          // simple progress indicator (not byte-accurate but good UX)
          setUploadPct(Math.round(((i + 1) / files.length) * 100));
        }
      }

      // 3) Update doc with attachments
      if (uploaded.length) {
        await updateDoc(doc(db, "evaluationRequests", docRef.id), {
          attachments: uploaded,
          lastUpdatedAt: serverTimestamp(),
        });
      }

      // 4) Reset UI
      setStatus({
        type: "ok",
        msg: "Request received. We’ll follow up with next steps shortly.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
      setFiles([]);
      formRef.current?.reset();
      setUploadPct(0);
    } catch (err) {
      setStatus({
        type: "error",
        msg: "Something went wrong sending your request. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section stone">
      <div className="container">
        <header className="section-head">
          <h1>Request an Evaluation</h1>
          <p className="muted">
            Share a few details and photos. We will follow up with next steps.
          </p>
          <p className="muted">
            {regionalContact.label}: {regionalContact.location}
            {regionalContact.phone && <> · <a href={`tel:${regionalContact.phone.replace(/[^+\d]/g, "")}`}>{regionalContact.phone}</a></>}
            {" · "}<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </header>

        <form ref={formRef} className="form" onSubmit={onSubmit}>
          <label className="field">
            <input
              type="text"
              required
              placeholder="Your Name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
            />
          </label>

          <label className="field">
            <input
              type="email"
              required
              placeholder="Email Address"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
          </label>

          <label className="field">
            <input
              type="tel"
              required
              placeholder="Phone Number"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
            />
          </label>

          <label className="field">
            <textarea
              rows="4"
              required
              placeholder="How Can We Help?"
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
            />
          </label>

          {/* Image uploads */}
          <label className="field">
            <div className="micro muted" style={{ marginBottom: 8 }}>
              Upload photos (up to {MAX_FILES}, {MAX_MB_EACH}MB each)
            </div>

            <input type="file" accept="image/*" multiple onChange={onPickFiles} />
          </label>

          {files.length > 0 && (
            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                marginBottom: 12,
              }}
            >
              {previews.map((p, idx) => (
                <div
                  key={p.url}
                  style={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.65)",
                  }}
                >
                  <div style={{ aspectRatio: "1 / 1", background: "#eee" }}>
                    <img
                      src={p.url}
                      alt={`Upload preview ${idx + 1}`}
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn"
                    onClick={() => removeFile(idx)}
                    style={{ width: "100%", borderRadius: 0 }}
                    disabled={busy}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <button className="btn primary" disabled={busy}>
            {busy ? "Sending..." : "Send Request"}
          </button>

          {busy && files.length > 0 && (
            <p className="micro muted" style={{ marginTop: 10 }}>
              Uploading photos: {uploadPct}%
            </p>
          )}

          {status.msg && (
            <p
              className={`micro ${status.type === "error" ? "" : "muted"}`}
              style={{ marginTop: 10 }}
            >
              {status.msg}
            </p>
          )}

          <p className="micro muted" style={{ marginTop: 10 }}>
            Requests are securely stored for review in our admin dashboard.
          </p>
        </form>
      </div>
    </section>
  );
}
